// OOXML property-extraction module: recovers the formatting mammoth cannot emit
// (font size, font color, Google-Docs w:shd highlight, line spacing, real
// numbering formats a./i./1.1, list start values) by reading the raw
// document/styles/numbering XML out of the .docx zip and re-attaching those
// props onto the mammoth-built Plate fragment, anchored by paragraph text.
//
// Handles both Word and Google Docs by construction — both emit this XML.
import { readDocxXml } from './readDocxXml';
import { extractCellShadings, extractParagraphs } from './extract';
import { applyOoxmlProps, stampWordListRestarts } from './applyOoxmlProps';

export type { ApplyOoxmlResult, ApplyOoxmlStats, OoxmlCell, OoxmlParagraph } from './types';
export { extractParagraphs, extractCellShadings } from './extract';
export { applyOoxmlProps, stampWordListRestarts } from './applyOoxmlProps';

/**
 * Enrich a mammoth-built Plate fragment with OOXML-only properties.
 *
 * Best-effort and additive: any failure to read/parse the .docx XML (or an
 * absent document.xml) returns the fragment unchanged, so the import never
 * regresses to worse-than-mammoth output.
 */
export async function applyOoxmlFidelity(arrayBuffer: ArrayBuffer, fragment: any[]): Promise<any[]> {
    try {
        const parts = await readDocxXml(arrayBuffer);
        const paragraphs = extractParagraphs(parts);
        const cells = extractCellShadings(parts);
        if (paragraphs.length === 0 && cells.length === 0) return fragment;
        const result = applyOoxmlProps(fragment, paragraphs, cells);
        // listRestart only where Word's real ordinal diverges from the
        // positional derivation (§2.1 Option A3) — same best-effort contract:
        // a failure here still returns the (already-mutated) enriched fragment.
        stampWordListRestarts(result.fragment, result.listOrdinals);
        return result.fragment;
    } catch {
        return fragment;
    }
}
